import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
  Box,
  Snackbar,
} from "@mui/material";
import { BackendBaseUrl } from "./global_varibles";
import { Calendar, Phone, User, CheckCircle, XCircle } from "lucide-react";

interface ScheduleDialogProps {
  open: boolean;
  handleClose: () => void;
}

function ScheduleDialog({ open, handleClose }: ScheduleDialogProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(""); // For displaying validation errors
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info" | "warning" | undefined>(undefined);
  const [open_snack, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseSnack = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    // Validation for empty fields
    if (!name || !phone) {
      setError("Both fields are required.");
      return;
    }
    // Validation for phone number length
    if (phone.length !== 10 || !/^\d{10}$/.test(phone)) {
      setError("Phone number must be exactly 10 digits.");
      return;
    }

    setError(""); // Clear any previous errors
    setIsSubmitting(true);

    try {
      const bkroute = `${BackendBaseUrl}mng-notifications`;
      const response = await fetch(bkroute, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: name, phone_num: phone }),
      });
      const response_data = await response.json();
      if (response_data.status === "success") {
        setMessageType("success")
        setMessage(response_data.message || "Pickup scheduled successfully!")
        setOpen(true)
      } else {
        setMessageType("error")
        setMessage(response_data.message || "Failed to schedule pickup")
        setOpen(true)
      }
    } catch (error) {
      setMessageType("error");
      setMessage("Internal server error. Please try again.");
      setOpen(true);
    } finally {
      setIsSubmitting(false);
    }

    handleClose(); // Close the dialog after submission
    setName("");
    setPhone("");
  };

  const handleCloseDialog = () => {
    setError(""); // Reset error message when closing dialog
    setName("");
    setPhone("");
    handleClose();
  };

  return (
    <Box>
      <Dialog 
        open={open} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(17,24,39,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(249, 115, 22, 0.2)',
            borderRadius: '16px',
            minWidth: '400px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        {/* Custom Dialog Title */}
        <DialogTitle sx={{ p: 0 }}>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white text-xl font-bold">Schedule Pickup</h2>
                <p className="text-white/80 text-sm">Book your laundry pickup service</p>
              </div>
            </div>
          </div>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <div className="p-6 space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-white text-sm font-semibold">
                <User className="w-4 h-4 text-orange-500" />
                Full Name
              </label>
              <TextField
                autoFocus
                type="text"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(249, 115, 22, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f97316',
                    },
                    '& input': {
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 500,
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-white text-sm font-semibold">
                <Phone className="w-4 h-4 text-orange-500" />
                Phone Number
              </label>
              <TextField
                type="tel"
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter 10-digit phone number"
                inputProps={{ maxLength: 10 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(249, 115, 22, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f97316',
                    },
                    '& input': {
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 500,
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </div>

            {/* Error Alert */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <p className="text-red-400 font-semibold">{error}</p>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5" />
                <div>
                  <p className="text-orange-400 font-semibold text-sm">Pickup Information</p>
                  <p className="text-gray-300 text-xs mt-1">
                    Our team will contact you within 24 hours to confirm the pickup time and location.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 0 }}>
          <div className="flex gap-3 p-6 pt-0 w-full">
            <Button
              onClick={handleCloseDialog}
              disabled={isSubmitting}
              sx={{
                flex: 1,
                py: 1.5,
                px: 4,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                flex: 2,
                py: 1.5,
                px: 4,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                border: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'rgba(249, 115, 22, 0.5)',
                  transform: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Scheduling...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Schedule Pickup
                </div>
              )}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={open_snack}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ 
          top: "100px !important",
          '& .MuiSnackbarContent-root': {
            minWidth: '300px',
          }
        }}
      >
        <Alert 
          severity={messageType} 
          onClose={handleCloseSnack}
          sx={{
            background: messageType === 'success' 
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
            color: 'white',
            borderRadius: '12px',
            border: `1px solid ${messageType === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              color: 'white',
            },
            '& .MuiAlert-action': {
              color: 'white',
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ScheduleDialog;
